import type { SidebarItemId } from "@productlane/lib/src/sidebar-layout"
import {
	combinedGroupOf,
	inboxTabForItem,
} from "@productlane/lib/src/sidebar-layout"

import type { InboxState } from "@/lib/states/global-state"

interface RoutedEntry {
	item: { href: string; highlightFor?: string[] }
}

/**
 * The single Inbox row gives way to the tab rows, and the Assistant to its own
 * section; Recommendations goes when the Agent page is closed.
 */
export function combinedSidebarOmit(agentShown: boolean): SidebarItemId[] {
	return agentShown ? ["inbox", "agent"] : ["inbox", "agent", "recommendations"]
}

export function isRouteActive(
	pathname: string,
	routes: readonly string[],
): boolean {
	return routes.some((route) => pathname.startsWith(route))
}

export function entryRoutes(entry: RoutedEntry): string[] {
	return [entry.item.href, ...(entry.item.highlightFor ?? [])]
}

/** A page reached through More takes a row above it while it is open, like Linear. */
export function promotedEntry<T extends RoutedEntry>(
	pathname: string,
	moreEntries: readonly T[],
): T | undefined {
	return moreEntries.find((entry) =>
		isRouteActive(pathname, entryRoutes(entry)),
	)
}

/** One stop on the left nav, in the order Tab walks it. */
export type CombinedNavStop =
	| { kind: "route"; id: string; href: string; routes: string[] }
	| { kind: "tab"; value: InboxState }
	| { kind: "view"; id: string }
	// An idea or chat row in the Assistant section.
	| { kind: "agent"; id: string; href: string }

/**
 * Splits the shown entries into the rows above the Workspace group and the
 * group itself, both in the layout's combined order.
 */
export function combinedNavOrder<T extends { id: string }>(
	order: readonly string[],
	entries: readonly T[],
): { top: T[]; workspace: T[] } {
	const rank = (entry: T) => {
		const index = order.indexOf(entry.id)
		return index === -1 ? order.length : index
	}
	const sorted = [...entries].sort((a, b) => rank(a) - rank(b))
	return {
		top: sorted.filter((entry) => combinedGroupOf(entry.id) === "top"),
		workspace: sorted.filter(
			(entry) => combinedGroupOf(entry.id) === "workspace",
		),
	}
}

export function buildCombinedNavStops({
	entries,
	promoted,
	viewIds,
	agentRows = [],
}: {
	entries: readonly (RoutedEntry & { id: string })[]
	promoted?: RoutedEntry & { id: string }
	viewIds: readonly string[]
	agentRows?: readonly { id: string; href: string }[]
}): CombinedNavStop[] {
	const toStop = (entry: RoutedEntry & { id: string }): CombinedNavStop => {
		const tab = inboxTabForItem(entry.id)
		if (tab) return { kind: "tab", value: tab as InboxState }
		return {
			kind: "route",
			id: entry.id,
			href: entry.item.href,
			routes: entryRoutes(entry),
		}
	}
	return [
		...entries.map(toStop),
		...(promoted ? [toStop(promoted)] : []),
		...viewIds.map((id) => ({ kind: "view" as const, id })),
		...agentRows.map((row) => ({
			kind: "agent" as const,
			id: row.id,
			href: row.href,
		})),
	]
}

export function activeCombinedNavIndex(
	stops: readonly CombinedNavStop[],
	{
		pathname,
		inboxState,
		activeCustomViewId,
		activeAgentRowId = null,
	}: {
		pathname: string
		inboxState: InboxState
		activeCustomViewId: string | null
		activeAgentRowId?: string | null
	},
): number {
	const onThreads = pathname.startsWith("/threads")
	return stops.findIndex((stop) => {
		if (stop.kind === "agent") return stop.id === activeAgentRowId
		if (stop.kind === "view") return onThreads && stop.id === activeCustomViewId
		if (stop.kind === "tab")
			return onThreads && !activeCustomViewId && stop.value === inboxState
		return isRouteActive(pathname, stop.routes)
	})
}

export function nextCombinedNavIndex(
	length: number,
	currentIndex: number,
	direction: "forward" | "backward",
): number {
	if (currentIndex === -1) return direction === "forward" ? 0 : length - 1
	return direction === "forward"
		? (currentIndex + 1) % length
		: (currentIndex - 1 + length) % length
}
